/* eslint-disable react-refresh/only-export-components -- co-located draft helpers consumed only by the editor in this feature; splitting them into a separate file adds noise without value */
import {
  DEFAULT_MAX_SEGMENTS,
  SEGMENTATION_STRATEGY_OPTIONS,
  type DocumentSegmentationRequest,
  type DocumentSegmentationResponse,
  type SegmentationStrategy,
} from '../api';

/**
 * Editor-local draft shape for a document type's segmentation policy. Unlike the wire DTOs it is
 * always a concrete object (never null) and uses empty strings instead of null for the optional
 * identifier hints, so the form inputs bind without null-juggling. Projected to/from the wire
 * shapes by the helpers below.
 */
export interface SegmentationDraft {
  enabled: boolean;
  strategy: SegmentationStrategy;
  /** Empty string = no identifier field declared. */
  identifierFieldName: string;
  /** Empty string = no explicit regex. */
  identifierPattern: string;
  maxSegments: number;
}

export const DEFAULT_SEGMENTATION_DRAFT: SegmentationDraft = {
  enabled: false,
  strategy: 'AiBoundary',
  identifierFieldName: '',
  identifierPattern: '',
  maxSegments: DEFAULT_MAX_SEGMENTS,
};

/** Hydrates the editor draft from the server response (null/absent = legacy single-document). */
export function segmentationDraftFromResponse(
  seg: DocumentSegmentationResponse | null | undefined,
): SegmentationDraft {
  if (seg === null || seg === undefined) {
    return DEFAULT_SEGMENTATION_DRAFT;
  }
  return {
    enabled: seg.enabled,
    strategy: (seg.strategy as SegmentationStrategy) || 'AiBoundary',
    identifierFieldName: seg.identifierFieldName ?? '',
    identifierPattern: seg.identifierPattern ?? '',
    maxSegments: seg.maxSegments > 0 ? seg.maxSegments : DEFAULT_MAX_SEGMENTS,
  };
}

/**
 * Builds the wire request for the segmentation policy, or `null` to OMIT it from the update
 * (server semantics: a null `segmentation` leaves the existing policy untouched). We only omit
 * when the type never had a policy AND the user left it off — that preserves the "never
 * configured = legacy single-document" state and avoids a pointless write that would flip the
 * read-back from `null` to `{enabled:false}`.
 */
export function segmentationRequestFromDraft(
  draft: SegmentationDraft,
  hadServerPolicy: boolean,
): DocumentSegmentationRequest | null {
  if (!draft.enabled && !hadServerPolicy) {
    return null;
  }
  const fieldName = draft.identifierFieldName.trim();
  const pattern = draft.identifierPattern.trim();
  return {
    enabled: draft.enabled,
    strategy: draft.strategy,
    identifierFieldName: fieldName === '' ? null : fieldName,
    identifierPattern: pattern === '' ? null : pattern,
    maxSegments: draft.maxSegments,
  };
}

interface SegmentationPanelProps {
  value: SegmentationDraft;
  onChange: (next: SegmentationDraft) => void;
  /** Field names of the current schema, offered as identifier-field options. */
  fieldNames: ReadonlyArray<string>;
  /**
   * Map of field name → regex pattern (null/undefined = no pattern configured on that field).
   * Used to warn when IdentifierRecurrence is selected but neither an explicit pattern in the
   * segmentation config nor a field-level pattern is available — the strategy will degrade to
   * single-document at runtime.
   */
  fieldPatterns?: Readonly<Record<string, string | null | undefined>>;
  disabled?: boolean;
}

/**
 * Opt-in toggle + configuration for document segmentation: splitting one uploaded file that is a
 * concatenation of several logical documents of the same type into N independent child documents.
 * The AI strategy is the type-agnostic default; the identifier hints only sharpen the recurring-
 * identifier strategy (and act as a hint to the AI path). Off by default — until enabled here a
 * type keeps the unchanged single-document flow.
 */
export function SegmentationPanel({
  value,
  onChange,
  fieldNames,
  fieldPatterns,
  disabled = false,
}: SegmentationPanelProps): JSX.Element {
  const update = (patch: Partial<SegmentationDraft>): void => onChange({ ...value, ...patch });
  const usesIdentifier = value.strategy === 'IdentifierRecurrence';
  const namedFields = fieldNames.filter((n) => n.trim() !== '');

  // Warn when IdentifierRecurrence is chosen but there is no usable regex pattern.
  // At runtime the activity falls back to "single_page" / "identifier_pattern_empty" in this case.
  const identifierMissingPattern =
    usesIdentifier &&
    value.identifierPattern.trim() === '' &&
    (value.identifierFieldName.trim() === '' ||
      !fieldPatterns?.[value.identifierFieldName] ||
      fieldPatterns[value.identifierFieldName]?.trim() === '');

  return (
    <div className="space-y-4">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-primary"
          checked={value.enabled}
          disabled={disabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <span>
          <span className="text-sm font-medium text-slate-900">Activar segmentación</span>
          <span className="mt-1 block text-xs text-slate-500">
            Cuando un fichero subido es en realidad la unión de varios documentos del mismo tipo
            (p. ej. varios despachos en un mismo PDF), se detectan y cada uno se procesa como un
            documento independiente. Apagado = el fichero se trata como un único documento.
          </span>
        </span>
      </label>

      {value.enabled ? (
        <div className="space-y-4 border-l-2 border-slate-100 pl-4">
          <div>
            <label className="label" htmlFor="seg-strategy">
              Estrategia de detección
            </label>
            <select
              id="seg-strategy"
              className="input"
              value={value.strategy}
              disabled={disabled}
              onChange={(e) => update({ strategy: e.target.value as SegmentationStrategy })}
            >
              {SEGMENTATION_STRATEGY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              La detección por IA funciona en cualquier tipo de documento. El identificador
              recurrente es determinista y más barato, pero solo sirve si cada documento tiene un
              código que se repite en sus páginas y cambia entre documentos.{' '}
              <strong>
                Elegir un campo identificador NO cambia la estrategia — debes seleccionar
                &ldquo;Por identificador&rdquo; como estrategia.
              </strong>
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="seg-id-field">
                Campo identificador {usesIdentifier ? '' : '(opcional)'}
              </label>
              <select
                id="seg-id-field"
                className="input"
                value={value.identifierFieldName}
                disabled={disabled}
                onChange={(e) => update({ identifierFieldName: e.target.value })}
              >
                <option value="">— ninguno —</option>
                {namedFields.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Campo del esquema cuyo valor se repite por página (p. ej. el MRN). Marca los límites
                entre documentos.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="seg-id-pattern">
                Patrón del identificador (regex, opcional)
              </label>
              <input
                id="seg-id-pattern"
                className="input font-mono text-sm"
                value={value.identifierPattern}
                disabled={disabled}
                placeholder="\\b\\d{2}[A-Z]{2}\\w+\\b"
                onChange={(e) => update({ identifierPattern: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Regex explícita que reconoce el identificador en el texto de una página. Si se omite,
                se usa el patrón del campo elegido (si tiene).
              </p>
            </div>
          </div>

          {identifierMissingPattern ? (
            <div
              role="alert"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              <strong>Patrón requerido:</strong> la estrategia &ldquo;Por identificador&rdquo;
              necesita una regex para reconocer el identificador en el texto. Añade un patrón
              explícito en el campo <em>Patrón del identificador</em> (arriba) o configura una
              regex en la definición del campo elegido. Sin patrón, el fichero se tratará como un
              único documento.
            </div>
          ) : null}

          <div>
            <label className="label" htmlFor="seg-max">
              Máximo de sub-documentos
            </label>
            <input
              id="seg-max"
              type="number"
              min={1}
              step={1}
              className="input w-32"
              value={value.maxSegments}
              disabled={disabled}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                update({ maxSegments: Number.isNaN(parsed) || parsed < 1 ? 1 : parsed });
              }}
            />
            <p className="mt-1 text-xs text-slate-500">
              Tope de seguridad: un fichero nunca se dividirá en más documentos que este número.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
